import { useState, useEffect } from 'react';
import _ from 'lodash';
import {
  Flex,
  Button,
  Box,
  Divider,
  Text,
  ProgressBar,
  Counter,
  Icon,
  Tooltip,
  Dialog,
  DialogContentContainer,
  Menu,
  MenuItem,
  MenuDivider,
} from '@vibe/core';
import { shift } from '@floating-ui/react-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import Frame from 'react-frame-component';
import { useEditor, EditorWidget } from '../hooks/use-editor';
import { PublishModal } from './PublishModal';
import { LabelsModal } from './LabelsModal';
import { ResetSettingsModal } from './ResetSettingsModal';
import { HelpModal } from './HelpModal';
import { DeleteWidgetModal } from './DeleteWidgetModal';
import { Viewer } from '@gorilla/widgets-viewer/app/viewer/Viewer';
import viewerStyles from '@gorilla/widgets-viewer/app/viewer/viewer.css?inline';
import fontsStyles from '@gorilla/widgets-viewer/public/fonts.css?inline';
import { BoardRulesModal } from '@gorilla/ui/src/components/BoardRulesModal';
import { ErrorBox as CommonErrorBox } from '@gorilla/ui/src/components/ErrorBox';
import { areBoardFiltersValid, getBoardFiltersCount } from '@gorilla/common/src/lib/rules';
import { MODE_TO_SETTINGS, LayoutsSettings, ColumnSettings } from './Settings';
import { mondayClient } from '../services/monday';
import ErrorBox from './ErrorBox';
import { PublishedIframe } from './PublishedIframe';
import { ShareBox } from './ShareBox';
import {
  Bullet,
  LearnMore,
  Alert,
  Completed,
  Board,
  DropdownChevronDown,
  DropdownChevronUp,
  Minimize,
  Open,
  Academy,
  Video,
  Calendar,
  Email,
  Upload,
  Translation,
  Idea,
  SettingsKnobs,
  Table,
  Workspace,
  Status,
} from '@vibe/icons';
import './editor.css';

{
  /* Advanced settings */
}
import { AdvancedSettingsModal } from './AdvancedSettingsModal';

type EditorProps = {
  widget: EditorWidget;
  onBackToList: () => void;
};

function formatTs(ts: number) {
  let newDate = formatDistanceToNowStrict(new Date(ts), { addSuffix: true });
  if (newDate === 'less than a minute ago') {
    newDate = 'Just now';
  }
  return newDate.charAt(0).toUpperCase() + newDate.slice(1);
}

function Editor({ widget, onBackToList }: EditorProps) {
  const [mobilePreview, setMobilePreview] = useState(false);
  const [helpMenuOpen, setHelpMenuOpen] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [showLabelsModal, setShowLabelsModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [helpState, setHelpState] = useState({ category: '', content: '' });
  const [lastUpdatedTime, setLastUpdatedTime] = useState<string>('');

  {
    /* Advanced settings */
  }
  const [showAdvancedModal, setShowAdvancedModal] = useState(false);

  const {
    setSettingsDraft,
    settings,
    setSetting,
    editorTab,
    setEditorTab,
    viewerData,
    path,
    widgetInfo,
    fetchWidgetInfo,
    isPublished,
    triggerUnpublish,
    isUnpublishing,
    mondayContext,
    mondayContextError,
    settingsServerError,
    resetSettings,
    board,
    executionsInfo,
    itemLoaderError,
    itemLoaderProgress,
  } = useEditor(widget);

  useEffect(() => {
    const updateLastUpdatedTime = () => {
      if (widgetInfo?.publishedAt) {
        setLastUpdatedTime(formatTs(widgetInfo?.publishedAt));
      }
    };

    const getIntervalDuration = () => {
      if (widgetInfo?.publishedAt) {
        const now = new Date();
        const lastUpdated = new Date(widgetInfo?.publishedAt);
        const timeDifference = now.getTime() - lastUpdated.getTime();
        const oneMinute = 60 * 1000;

        if (timeDifference < oneMinute) {
          return 1000; // 1 second
        }
      }
      return 60000; // 60 seconds
    };

    updateLastUpdatedTime();
    const intervalId = setInterval(updateLastUpdatedTime, getIntervalDuration());

    return () => clearInterval(intervalId);
  }, [widgetInfo?.publishedAt]);

  if (_.get(mondayContext, 'user.isGuest') === true) {
    return <ErrorBox message="Guests are not allowed to use this app" />;
  }

  if (_.get(mondayContext, 'user.isViewOnly') === true) {
    return <ErrorBox message="Viewers are not allowed to use this app" />;
  }

  if (mondayContextError) {
    return <ErrorBox message={mondayContextError?.message} />;
  }

  if (settingsServerError) {
    return <ErrorBox message={settingsServerError?.message} />;
  }

  const modals = (
    <>
      {showFilters && board ? (
        <BoardRulesModal
          isOpen={showFilters}
          board={board}
          hideOrderBy={true}
          inititalBoardFilters={settings && (settings.boardFilters as any)}
          onClose={(newBoardFilters) => {
            if (newBoardFilters !== null && newBoardFilters !== undefined) {
              setSetting('boardFilters', newBoardFilters);
            }

            setShowFilters(false);
          }}
        />
      ) : null}

      {showAdvancedModal && settings ? (
        <AdvancedSettingsModal
          show={showAdvancedModal}
          onClose={(newSettings) => {
            setShowAdvancedModal(false);
            if (newSettings) {
              setSettingsDraft(newSettings);
            }
          }}
          settings={settings}
        />
      ) : null}
    </>
  );

  if (itemLoaderError) {
    return (
      <>
        {modals}
        <CommonErrorBox error={itemLoaderError} mondayClient={mondayClient} />
        <Box
          margin="medium"
          padding="large"
          backgroundColor="greyBackgroundColor"
          border={true}
          borderColor="layoutBorderColor"
          rounded="small"
        >
          <Flex gap={24} justify="space-between">
            <Flex gap={10}>
              <Text type="text2" color="primary" ellipsis={false}>
                Disable all advanced settings to avoid any issues that may be caused by them and check your Board Filters.
              </Text>
            </Flex>

            <Flex gap={4}>
              <Button kind="primary" size="medium" color="fixed-dark" leftIcon={SettingsKnobs} onClick={() => setShowAdvancedModal(true)}>
                Advanced settings
              </Button>

              {board ? (
                <Button size="medium" color="fixed-dark" leftIcon={Board} onClick={() => setShowFilters(true)}>
                  Board Filters
                </Button>
              ) : null}
            </Flex>
          </Flex>
        </Box>
      </>
    );
  }

  const itemLoaderProgressNormalized = itemLoaderProgress || 0;

  // widgets created before names existed fall back to the board name
  const widgetName = settings?.name || board?.name || 'Widget';

  if (!viewerData || !board) {
    return (
      <div
        style={{
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100vw',
          height: '100vh',
        }}
      >
        <Flex gap={12} direction="column" align="center" style={{ width: '150px' }}>
          <div
            style={{
              color: '#0073ea',
            }}
          >
            <Flex gap={8} align="center">
              {itemLoaderProgressNormalized < 100 ? (
                <div className="LoadingIconContainer" style={{ lineHeight: 0 }}>
                  <div className="LoadingIcon LoadingIcon--1">
                    <Icon icon={Table} size={20} />
                  </div>
                  <div className="LoadingIcon LoadingIcon--2">
                    <Icon icon={Workspace} size={18} />
                  </div>
                  <div className="LoadingIcon LoadingIcon--3">
                    <Icon icon={Status} size={17} />
                  </div>
                </div>
              ) : null}

              {itemLoaderProgressNormalized === 100 ? (
                <div style={{ lineHeight: 0 }}>
                  <Icon icon={Completed} size={18} />
                </div>
              ) : null}

              <div>{itemLoaderProgressNormalized < 100 ? 'Loading widget' : 'Loading widget'}</div>
            </Flex>
          </div>
          <ProgressBar value={itemLoaderProgressNormalized} size="small" barStyle="primary" />
        </Flex>
      </div>
    );
  }

  if (!settings) {
    return null;
  }

  let ModeSettingsComponent = null;

  if (settings.mode in MODE_TO_SETTINGS) {
    ModeSettingsComponent = MODE_TO_SETTINGS[settings.mode];
  }

  // replace /fonts/ with ../fonts/ in fontsStyles
  const fontsStylesNormalized = fontsStyles.replace(/\/fonts\//g, '../fonts/');
  const allViewerStyles = `${fontsStylesNormalized}\n${viewerStyles}`;

  return (
    <>
      {modals}
      {showHelpModal ? (
        <HelpModal
          show={showHelpModal}
          category={helpState.category}
          content={helpState.content}
          onClose={() => {
            setShowHelpModal(false);
            setHelpState({ category: '', content: '' });
          }}
        />
      ) : null}
      {showResetModal ? (
        <ResetSettingsModal
          show={showResetModal}
          onClose={(reset) => {
            if (reset) {
              resetSettings();
            }
            setShowResetModal(false);
          }}
        />
      ) : null}
      {showDeleteModal ? (
        <DeleteWidgetModal
          show={showDeleteModal}
          isUnpublishing={isUnpublishing}
          onClose={async (doDelete) => {
            if (doDelete) {
              await triggerUnpublish();
              fetchWidgetInfo();
            }

            setShowDeleteModal(false);
          }}
        />
      ) : null}
      {/* PUBLISH MODAL */}
      {showPublishModal && path ? (
        <PublishModal
          show={showPublishModal}
          boardInfo={viewerData.boardInfo}
          items={viewerData.items}
          assets={viewerData.assets}
          path={path}
          settings={settings}
          isPublished={isPublished}
          widgetInfo={widgetInfo}
          onClose={() => {
            fetchWidgetInfo();
            setShowPublishModal(false);
          }}
        />
      ) : null}
      {/* LABELS MODAL */}
      {showLabelsModal ? (
        <LabelsModal
          show={showLabelsModal}
          customLabels={settings.customLabels || {}}
          onClose={(newCustomLabels) => {
            if (newCustomLabels !== undefined) {
              setSetting('customLabels', newCustomLabels);
            }

            setShowLabelsModal(false);
          }}
        />
      ) : null}
      <div className="Datagrid">
        <div className="Bar">
          <Flex gap={16} align="center" justify="start">
            <Flex gap={24} style={{ fontSize: '13px', lineHeight: '18px' }}>
              <>
                <div style={{ width: '100px' }}>
                  <Flex gap={10}>
                    <Box>
                      <Flex gap={4} align="center">
                        <strong style={{ fontSize: '10px', fontWeight: 600, whiteSpace: 'nowrap' }}>Widget name</strong>
                      </Flex>
                      <span
                        style={{ display: 'block', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={widgetName}
                      >
                        {widgetName}
                      </span>
                    </Box>
                  </Flex>
                </div>
                <div style={{ height: '68px' }}>
                  <Divider direction="vertical" withoutMargin />
                </div>
              </>
              <div style={{ width: '100px' }}>
                <Flex gap={10}>
                  <Box>
                    <Flex gap={4} align="center">
                      <strong style={{ fontSize: '10px', fontWeight: 600, whiteSpace: 'nowrap' }}>Widget status</strong>
                    </Flex>
                    <span className="Bar--Value">
                      {widgetInfo?.publishedAt ? 'Published' : 'Unpublished'}{' '}
                      <span className={widgetInfo?.publishedAt ? 'Bar--Indicator Published' : 'Bar--Indicator Draft'}>
                        <Icon icon={Bullet} size={16} style={{ transform: 'translate3d(0,1px,0)' }} />
                      </span>
                    </span>
                  </Box>
                </Flex>
              </div>
              <div style={{ height: '68px' }}>
                <Divider direction="vertical" withoutMargin />
              </div>
              <div style={{ width: '100px' }}>
                <Flex gap={10}>
                  <Box>
                    <Flex gap={4} align="center">
                      <strong style={{ fontSize: '10px', fontWeight: 600, whiteSpace: 'nowrap' }}>Monthly automations</strong>
                    </Flex>
                    <span className="Bar--Value">
                      {executionsInfo
                        ? `${executionsInfo.executionsLimit - executionsInfo.executionsCount} of ${executionsInfo.executionsLimit} left`
                        : 'N/A'}{' '}
                    </span>
                  </Box>
                </Flex>
              </div>
              <div style={{ height: '68px' }}>
                <Divider direction="vertical" withoutMargin />
              </div>
            </Flex>
            <div style={{ flex: 1 }}>
              <Flex justify="start" style={{ position: 'relative' }}>
                {widgetInfo?.publishedAt ? (
                  <>
                    <div style={{ marginLeft: '10px', width: '120px', fontSize: '13px', lineHeight: '18px' }}>
                      <Flex gap={10}>
                        <Box>
                          <Flex gap={4} align="center">
                            <strong style={{ fontSize: '10px', fontWeight: 600, whiteSpace: 'nowrap' }}>Widget published</strong>
                          </Flex>
                          <span className="Bar--Value">
                            {/* {widgetInfo?.publishedAt ? `${formatDistanceToNowStrict(new Date(widgetInfo.publishedAt))} ago` : ''} */}
                            {lastUpdatedTime}
                          </span>
                        </Box>
                      </Flex>
                    </div>
                    <div style={{ marginRight: '14px', height: '68px' }}>
                      <Divider direction="vertical" withoutMargin />
                    </div>
                  </>
                ) : null}
                <Dialog
                  zIndex={2000}
                  content={
                    <DialogContentContainer
                      type="popover"
                      size="none"
                      style={{
                        overflow: 'hidden',
                        padding: '2px 0',
                      }}
                    >
                      <Menu size="medium">
                        <MenuItem
                          title="Widget examples"
                          icon={Idea}
                          onClick={() => {
                            setShareMenuOpen(false);
                            setHelpMenuOpen(false);
                            window.open('https://getgorilla.app/products/board-to-website-widget/examples', '_blank');
                          }}
                          className="customMenuItem"
                        />
                        <MenuDivider className="customMenuDivider" />
                        <MenuItem
                          title="Documentation"
                          icon={LearnMore}
                          onClick={() => {
                            setShareMenuOpen(false);
                            setHelpMenuOpen(false);
                            setShowHelpModal(true);
                          }}
                          className="customMenuItem"
                        />
                        <MenuItem
                          title="How to use"
                          icon={Academy}
                          onClick={() => {
                            setShareMenuOpen(false);
                            setHelpMenuOpen(false);
                            window.open('https://getgorilla.app/products/board-to-website-widget/how-to-use', '_blank');
                          }}
                          className="customMenuItem"
                        />
                        <MenuItem
                          title="Video tutorials"
                          icon={Video}
                          onClick={() => {
                            setShareMenuOpen(false);
                            setHelpMenuOpen(false);
                            window.open('https://www.youtube.com/playlist?list=PLNt6YgM15GuExPehdm3in8ezQTB9kTUJ6', '_blank');
                          }}
                          className="customMenuItem"
                        />
                        <MenuDivider className="customMenuDivider" />
                        <MenuItem
                          title="Book a demo"
                          icon={Calendar}
                          onClick={() => {
                            setShareMenuOpen(false);
                            setHelpMenuOpen(false);
                            window.open('https://cal.com/gorilla-apps/board-to-website-widget-demo', '_blank');
                          }}
                          className="customMenuItem"
                        />
                        <MenuItem
                          title="Contact us"
                          icon={Email}
                          onClick={() => {
                            setShareMenuOpen(false);
                            setHelpMenuOpen(false);
                            window.open('https://getgorilla.app/contact', '_blank');
                          }}
                          className="customMenuItem"
                        />
                      </Menu>
                    </DialogContentContainer>
                  }
                  middleware={[shift({ mainAxis: false })]}
                  showTrigger={[]}
                  open={helpMenuOpen}
                  instantShowAndHide
                  moveBy={{ main: 0, secondary: 60 }}
                  // @ts-ignore
                  position="bottom"
                  onClickOutside={() => {
                    setShareMenuOpen(false);
                    setHelpMenuOpen(false);
                  }}
                >
                  <Button
                    kind="tertiary"
                    size="medium"
                    rightIcon={helpMenuOpen ? DropdownChevronUp : DropdownChevronDown}
                    onClick={() => {
                      setShareMenuOpen(false);
                      setHelpMenuOpen(!helpMenuOpen);
                    }}
                  >
                    Help
                  </Button>
                </Dialog>
                {isPublished && widgetInfo?.id ? (
                  <Dialog
                    zIndex={2000}
                    content={
                      <DialogContentContainer
                        type="popover"
                        size="none"
                        style={{
                          overflow: 'hidden',
                          padding: '2px 0',
                        }}
                      >
                        <ShareBox widgetId={widgetInfo?.id} hideBorder={true} hideBackground={true} isMenu={true} />
                      </DialogContentContainer>
                    }
                    middleware={[shift({ mainAxis: false })]}
                    showTrigger={[]}
                    hideTrigger={[]}
                    open={shareMenuOpen}
                    instantShowAndHide
                    moveBy={{ main: 0, secondary: 60 }}
                    position="bottom"
                    onClickOutside={() => {
                      setShareMenuOpen(false);
                      setHelpMenuOpen(false);
                    }}
                  >
                    <Button
                      kind="tertiary"
                      size="medium"
                      rightIcon={shareMenuOpen ? DropdownChevronUp : DropdownChevronDown}
                      onClick={() => {
                        setShareMenuOpen(!shareMenuOpen);
                        setHelpMenuOpen(false);
                      }}
                    >
                      Share
                    </Button>
                  </Dialog>
                ) : null}
              </Flex>
            </div>
            <Box>
              <Flex gap={12}>
                {isPublished ? (
                  <Button
                    disabled={!isPublished}
                    size="medium"
                    kind="secondary"
                    color="primary"
                    onClick={() => {
                      //createSignal'delete_widget');
                      setShowDeleteModal(true);
                    }}
                  >
                    Unpublish
                  </Button>
                ) : null}
                <Button
                  leftIcon={Upload}
                  size="medium"
                  kind="primary"
                  color="primary"
                  onClick={() => {
                    //createSignal('publish_widget_modal');
                    setShowPublishModal(true);
                  }}
                >
                  Publish
                </Button>
              </Flex>
            </Box>
          </Flex>
        </div>

        <div className="Container">
          <div className="Sidebar">
            <LayoutsSettings
              settings={settings}
              setSetting={setSetting}
              boardInfo={viewerData.boardInfo}
              setHelpState={setHelpState}
              settingCategory=""
              setShowHelpModal={setShowHelpModal}
            />

            {ModeSettingsComponent ? (
              <ModeSettingsComponent
                settings={settings}
                setSetting={setSetting}
                boardInfo={viewerData.boardInfo}
                setHelpState={setHelpState}
                settingCategory=""
                setShowHelpModal={setShowHelpModal}
              />
            ) : null}

            {ModeSettingsComponent && settings.mode !== 'events' ? (
              <ColumnSettings
                settings={settings}
                setSetting={setSetting}
                boardInfo={viewerData.boardInfo}
                setHelpState={setHelpState}
                settingCategory=""
                setShowHelpModal={setShowHelpModal}
              />
            ) : null}

            <div className="SidebarWidget SidebarWidget--Collapsed">
              <Box className="SidebarWidget--Header">
                <Flex gap={8} direction="column" align="start">
                  <Flex
                    gap={8}
                    style={{ width: '100%' }}
                    onClick={() => {
                      setShowFilters(true);
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: areBoardFiltersValid(board, settings.boardFilters as any) ? '#323338' : '#d83a52',
                      }}
                      className="DropdownIcon"
                    >
                      <Icon icon={areBoardFiltersValid(board, settings.boardFilters as any) ? Board : Alert} size={20} />
                    </div>
                    <h5
                      style={{
                        flex: 1,
                        color: areBoardFiltersValid(board, settings.boardFilters as any) ? '#323338' : '#d83a52',
                      }}
                    >
                      Board filters
                    </h5>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: '#323338',
                      }}
                      className="DropdownIcon"
                    >
                      <Counter
                        count={getBoardFiltersCount(board, settings.boardFilters as any)}
                        size="large"
                        color={areBoardFiltersValid(board, settings.boardFilters as any) ? 'dark' : 'negative'}
                        className="Counter--Custom"
                      />
                    </div>
                  </Flex>
                </Flex>
              </Box>
            </div>

            <div className="SidebarWidget SidebarWidget--Collapsed">
              <Box className="SidebarWidget--Header">
                <Flex gap={8} direction="column" align="start">
                  <Flex
                    gap={8}
                    style={{ width: '100%' }}
                    onClick={() => {
                      setShowLabelsModal(true);
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: '-2px',
                        color: '#323338',
                      }}
                      className="DropdownIcon"
                    >
                      <Icon icon={Translation} size={22} />
                    </div>
                    <h5 style={{ flex: 1 }}>Text labels</h5>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: '#323338',
                      }}
                      className="DropdownIcon"
                    >
                      <Counter
                        count={Object.values(settings.customLabels || {}).filter((val) => val).length}
                        size="large"
                        color="dark"
                        className="Counter--Custom"
                      />
                    </div>
                  </Flex>
                </Flex>
              </Box>
            </div>

            <div className="SidebarWidget SidebarWidget--Collapsed">
              <Box className="SidebarWidget--Header">
                <Flex gap={8} direction="column" align="start">
                  <Flex
                    gap={8}
                    style={{ width: '100%' }}
                    onClick={() => {
                      setShowAdvancedModal(true);
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: '-2px',
                        color: '#323338',
                      }}
                      className="DropdownIcon"
                    >
                      <Icon icon={SettingsKnobs} size={20} />
                    </div>
                    <h5 style={{ flex: 1 }}>Advanced settings</h5>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: '#323338',
                      }}
                      className="DropdownIcon"
                    >
                      <Counter
                        count={Number(Boolean(settings?.enableSubitemsSupport)) + Number(Boolean(settings?.enableFormulaSupport))}
                        size="large"
                        color="dark"
                        className="Counter--Custom"
                      />
                    </div>
                  </Flex>
                </Flex>
              </Box>
            </div>

            <div style={{ padding: '24px', backgroundColor: '#fff' }}>
              <Flex gap={12}>
                <Button
                  kind="secondary"
                  size="small"
                  onClick={() => {
                    setShowResetModal(true);
                  }}
                  style={{ width: '100%' }}
                >
                  Reset settings
                </Button>
                <Button
                  kind="tertiary"
                  size="small"
                  onClick={() => {
                    onBackToList();
                  }}
                  style={{ width: '100%' }}
                >
                  Manage widgets
                </Button>
              </Flex>
            </div>

            <div className="Teaser">
              <h4 style={{ fontSize: '20px', fontWeight: '600', lineHeight: '24px', WebkitFontSmoothing: 'antialiased' }}>
                Build your Client <br />
                Portal right here <br />
                on monday.com
              </h4>
              <p style={{ marginBottom: '20px', fontSize: '14px', lineHeight: '20px' }}>
                <strong style={{ display: 'block', margin: '20px 0', fontWeight: 400 }}>
                  Are you interested in building secure, branded and password protected Client Portals?{' '}
                </strong>
              </p>
              <Button
                size="medium"
                kind="primary"
                color="on-primary-color"
                onClick={() => {
                  window.open('https://getgorilla.app/products/client-portal', '_blank');
                }}
                style={{ width: '100%', color: '#6161FF' }}
              >
                Learn more
              </Button>
              <ul style={{ listStyleType: 'none', margin: '20px 0 0 0', padding: 0 }}>
                <li>
                  <Icon icon={Completed} style={{ transform: 'translate3d(-3px,2px,0)' }} /> 100% no-code solution
                </li>
                <li>
                  <Icon icon={Completed} style={{ transform: 'translate3d(-3px,2px,0)' }} /> Hosted on monday.com
                </li>
                <li>
                  <Icon icon={Completed} style={{ transform: 'translate3d(-3px,2px,0)' }} /> Designed for collaboration
                </li>
              </ul>
            </div>
            <div
              style={{
                overflow: 'hidden',
                margin: '0 0 42px 42px',
                borderTopLeftRadius: '5px',
                borderBottomLeftRadius: '5px',
                boxShadow: '0px 5px 20px rgba(0,0,0,0.15)',
                lineHeight: 0,
              }}
            >
              <a href="https://clientportalbuilder.com" target="_blank" style={{ lineHeight: 0 }}>
                <img
                  src="/client-portal-builder-dashboard-aejrf2w7bnmce4h.webp?v=1"
                  width="2400"
                  height="1470"
                  alt=""
                  style={{ width: '140%', height: 'auto' }}
                />
              </a>
            </div>
          </div>

          <div className="Content">
            {widgetInfo?.id ? (
              <>
                <div className="FrameWrapper--Header">
                  <Tooltip
                    content="The preview of your widgets current working draft. Reflecting all changes in real-time."
                    position="right"
                    //zIndex={2000}
                    showDelay={100}
                    hideDelay={0}
                    showOnDialogEnter={false}
                  >
                    <div
                      className={editorTab === 'preview' ? 'FrameWrapper--Label FrameWrapper--LabelActive' : 'FrameWrapper--Label'}
                      onClick={() => {
                        setEditorTab('preview');
                      }}
                    >
                      Preview
                    </div>
                  </Tooltip>
                  {widgetInfo.publishedAt ? (
                    <Tooltip
                      content="The current published version of your website widget. Click to compare with your preview."
                      position="right"
                      //zIndex={2000}
                      showDelay={100}
                      hideDelay={0}
                      showOnDialogEnter={false}
                    >
                      <div
                        className={editorTab === 'published' ? 'FrameWrapper--Label FrameWrapper--LabelActive' : 'FrameWrapper--Label'}
                        onClick={() => {
                          setEditorTab('published');
                        }}
                      >
                        Published
                      </div>
                    </Tooltip>
                  ) : null}
                  <div className="FrameWrapper--Header__Actions">
                    <Flex gap={16}>
                      <Flex style={{ position: 'relative' }}>
                        <Tooltip content="Desktop" position="bottom" zIndex={2000} showOnDialogEnter={false}>
                          <Button
                            disabled={!mobilePreview}
                            preventClickAnimation
                            rightFlat
                            leftIcon={Open}
                            size="xs"
                            kind={!mobilePreview ? 'primary' : 'secondary'}
                            onClick={() => {
                              setMobilePreview(false);
                            }}
                            style={{
                              padding: '5px 5px 5px 12px',
                              height: '34px',
                            }}
                            className="Button--PreviewToggle"
                          >
                            &nbsp;
                          </Button>
                        </Tooltip>
                        <Tooltip content="Mobile" position="bottom" zIndex={2000} showOnDialogEnter={false}>
                          <Button
                            disabled={mobilePreview}
                            preventClickAnimation
                            leftFlat
                            rightIcon={Minimize}
                            size="xs"
                            kind={mobilePreview ? 'primary' : 'secondary'}
                            onClick={() => {
                              setMobilePreview(true);
                            }}
                            style={{
                              padding: '5px 12px 5px 5px',
                              height: '34px',
                            }}
                            className="Button--PreviewToggle"
                          >
                            &nbsp;
                          </Button>
                        </Tooltip>
                      </Flex>
                    </Flex>
                  </div>
                </div>
                <div className="FrameWrapper">
                  {editorTab === 'preview' ? (
                    <Frame
                      id="viewer-iframe"
                      className={mobilePreview ? 'viewer-iframe--mobile' : ''}
                      initialContent='<!DOCTYPE html><html><head><link href="/fontawesome/css/fontawesome.min.css" rel="stylesheet"><link href="/fontawesome/css/regular.min.css" rel="stylesheet"></link><link href="/fontawesome/css/solid.min.css" rel="stylesheet"></link></head><body><div id="Content"></div></body></html>'
                      mountTarget="#Content"
                    >
                      <Viewer
                        boardInfo={viewerData.boardInfo}
                        boardItems={viewerData.items}
                        assets={viewerData.assets}
                        settings={settings}
                        mode="preview"
                        widgetId={widgetInfo.id}
                        styles={allViewerStyles}
                        previewTumbnailGeneratorUrl={`${import.meta.env.VITE_PUBLISHER_ENDPOINT_URL}/preview-thumbnail`}
                      />
                    </Frame>
                  ) : (
                    <PublishedIframe src={`${import.meta.env.VITE_WIDGETS_VIEWER_URL}/${widgetInfo.id}`} mobilePreview={mobilePreview} />
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default Editor;
